/**
 * メンバー四半期 KPI テーブル
 * 当 Q（Qまとめシート）の月別 開発pt / 戦闘力 の目標・実績・達成率を表示し、
 * ドロップダウンで過去 Q へ遡れる。データは戦闘力サマリー (GET /combat-power) と同一。
 * 左に月間pt カード（ピクセルフォント。四半期テーブルとは独立した月プルダウンで、availableQuarters
 * を展開した全過去月へ切り替え、その月の実績pt と明細を参照できる）、右に四半期 KPI カード
 * （開発pt / 戦闘力 の 2 つの小テーブルを左右並び）を 1:3 で配置する。
 * /members/$email と /my-page の両方から MemberDetailContent 経由で使用
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー個人の四半期 KPI テーブル（月別 開発pt 目標/実績/達成率 + 戦闘力 稼働日数/期待値/実績(当月は処理中pt 込みを括弧併記)/差分(実績−期待値、当月は処理中pt 込み実績が base) + Q合計(戦闘力は Q合計pt ÷ Q合計稼働日数)、過去 Q 選択、月選択で過去月の実績pt/明細を参照できる月間pt カード）
 * @graph-connects useMemberQuarterlyKpi [calls] メンバー四半期KPIフック
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { useMemberQuarterlyKpi } from '../../hooks/useMember.js';
import { ErrorState } from '../feedback/ErrorState/index.js';
import { combatPowerAchievementBadgeClass } from './kpi/WeeklyPointTable.js';
import { CurrentMonthPtDetailModal } from './CurrentMonthPtDetailModal.js';
import { formatNum } from './combat-power/combat-power-utils.js';
import { jstCurrentYm, pastMonthOptionsOf, quarterParamOfMonth } from '../../lib/fiscal-quarter.js';
import type { MemberQuarterlyKpiMonth, MemberQuarterlyKpiResponse } from '../../types/api.js';

/** @graph-connects none */
interface MemberQuarterlyKpiTableProps {
  /** メンバーのメールアドレス */
  email: string;
}

/** 月初日 (YYYY-MM-01) → "YYYY/M" 表示 @graph-connects none */
function monthLabel(month: string): string {
  return `${String(parseInt(month.slice(0, 4), 10))}/${String(parseInt(month.slice(5, 7), 10))}`;
}

/** 戦闘力・稼働日数（小数第1位、末尾ゼロ省略）表示。null は '-' @graph-connects none */
function formatPower(value: number | null): string {
  return formatNum(value, 1);
}

/** 列ヘッダ（指標名。数値と同じ右寄せ） @graph-connects none */
const HEADER_CELL_CLASS =
  'py-1.5 px-2.5 text-right text-[11px] font-semibold whitespace-nowrap text-[var(--color-text-secondary)]';
/** 行ヘッダ（月ラベル列） @graph-connects none */
const ROW_LABEL_CLASS = 'py-1.5 px-2.5 text-left whitespace-nowrap';
/** 数値セル @graph-connects none */
const CELL_CLASS = 'py-1.5 px-2.5 text-[var(--color-text-primary)] text-right whitespace-nowrap';
/** 月ラベル（行ヘッダのピル）共通クラス @graph-connects none */
const MONTH_PILL_CLASS =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none';
/**
 * Q合計 行。bg-elevated はライトで濃いめグレー / ダークで明るめグレーになり
 * どちらのテーマでも月行と落ち着いて区別できる
 * @graph-connects none
 */
const TOTAL_ROW_CLASS = 'bg-[var(--color-bg-elevated)] font-bold';
/** 開発pt テーブルのヘッダ行色（インディゴ。文字色はライト/ダーク対応の CSS 変数） @graph-connects none */
const PT_HEADER_ROW_CLASS = 'bg-[#6366F1]/15';
/** 戦闘力 テーブルのヘッダ行色（アンバー） @graph-connects none */
const POWER_HEADER_ROW_CLASS = 'bg-[#F59E0B]/15';
/** @graph-connects none */
const RATE_BADGE_CLASS =
  'inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none';

/** 達成率バッジ（null は '-'） @graph-connects none */
function RateBadge({ rate, inProgress }: { rate: number | null; inProgress: boolean }) {
  if (rate === null) return <>-</>;
  return (
    <span className={`${RATE_BADGE_CLASS} ${combatPowerAchievementBadgeClass(rate, inProgress)}`}>
      {`${String(rate)}%`}
    </span>
  );
}

/**
 * 戦闘力 差分（実績 − 期待値）。どちらか null なら null。
 * 全体ビューのサマリー (TeamTable) と同じく事前丸めはせず生の差分を返し、
 * 色判定・符号・表示桁は DiffText / formatNum 側に委ねる（負ゼロによる挙動差を避ける）
 * @graph-connects none
 */
function powerDiff(actual: number | null, expected: number | null): number | null {
  if (actual === null || expected === null) return null;
  return actual - expected;
}

/**
 * 当月の処理中pt を含めた実績戦闘力 = actualPower + 処理中pt ÷ 稼働日数。
 * 全体ビューの戦闘力サマリー (TeamTable) の totalPower（(完了pt + 処理中pt) ÷ 稼働日数）と同値。
 * 当月以外 / 処理中pt なし / 稼働日数 0 以下 / actualPower null は括弧併記・差分 base 切替をしないため null。
 * @graph-connects none
 */
function actualPowerWithWip(m: MemberQuarterlyKpiMonth): number | null {
  if (
    !m.isCurrentMonth ||
    m.actualPower === null ||
    m.wipPt === null ||
    m.wipPt <= 0 ||
    m.businessDays === null ||
    m.businessDays <= 0
  ) {
    return null;
  }
  return m.actualPower + m.wipPt / m.businessDays;
}

/**
 * 戦闘力 差分テキスト（実績 − 期待値）。null は '-'。
 * 全体ビューの戦闘力サマリー (TeamTable) と同形式: 0 以上は緑・未満は赤の色文字（符号付き）。
 * 色は Tailwind 任意値クラスで表現する（本 app の CSS Rules によりインラインスタイル禁止）。
 * バッジ化はしない。合計セルの太字は親 td の font-bold を継承する
 * @graph-connects none
 */
function DiffText({ diff }: { diff: number | null }) {
  if (diff === null) return <>-</>;
  return (
    <span className={diff >= 0 ? 'text-[#16a34a]' : 'text-[#ef4444]'}>
      {`${diff >= 0 ? '+' : ''}${formatNum(diff, 1)}`}
    </span>
  );
}

/** 月ラベルのピル（当月はアクセント色） @graph-connects none */
function MonthPill({ month }: { month: MemberQuarterlyKpiMonth }) {
  return (
    <span
      className={`${MONTH_PILL_CLASS} ${
        month.isCurrentMonth
          ? 'bg-[var(--color-accent-primary)] text-white'
          : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
      }`}
    >
      {monthLabel(month.month)}
    </span>
  );
}

/**
 * 月が行・指標が列の小テーブル（開発pt / 戦闘力 共通の骨格）
 * @graph-connects none
 */
function MetricTable({
  sectionLabel,
  sectionLabelClass,
  headerRowClass,
  columns,
  months,
  renderMonthCells,
  renderTotalCells,
  totalLabel,
}: {
  sectionLabel: string;
  sectionLabelClass: string;
  headerRowClass: string;
  columns: string[];
  months: MemberQuarterlyKpiMonth[];
  renderMonthCells: (m: MemberQuarterlyKpiMonth) => ReactNode[];
  renderTotalCells: () => ReactNode[];
  totalLabel: string;
}) {
  return (
    <div className="flex-1 min-w-0 overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className={`border-b border-[var(--color-border)] ${headerRowClass}`}>
            <th
              className={`py-1.5 px-2.5 text-left text-[11px] font-bold tracking-wider whitespace-nowrap ${sectionLabelClass}`}
            >
              {sectionLabel}
            </th>
            {columns.map((col) => (
              <th key={col} className={HEADER_CELL_CLASS}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr
              key={m.month}
              className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
            >
              <th scope="row" className={ROW_LABEL_CLASS}>
                <MonthPill month={m} />
              </th>
              {renderMonthCells(m).map((cell, i) => (
                <td key={columns[i]} className={CELL_CLASS}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          <tr className={TOTAL_ROW_CLASS}>
            <th
              scope="row"
              className={`${ROW_LABEL_CLASS} text-[11px] font-bold text-[var(--color-text-primary)]`}
            >
              {totalLabel}
            </th>
            {renderTotalCells().map((cell, i) => (
              <td key={columns[i]} className={`${CELL_CLASS} font-bold`}>
                {cell}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** テーブル本体（開発pt / 戦闘力 の 2 テーブルを左右並び） @graph-connects none */
function QuarterlyKpiTableBody({ data }: { data: MemberQuarterlyKpiResponse }) {
  const { t } = useTranslation();
  const totalLabel = t('members.quarterlyKpi.qTotal');

  return (
    <div data-testid="member-quarterly-kpi-table">
      <div className="flex flex-col lg:flex-row gap-x-8 gap-y-4">
        <MetricTable
          sectionLabel={t('members.quarterlyKpi.developPtSection')}
          sectionLabelClass="text-[var(--color-qkpi-pt)]"
          headerRowClass={PT_HEADER_ROW_CLASS}
          columns={[
            t('members.quarterlyKpi.target'),
            t('members.quarterlyKpi.actual'),
            t('members.quarterlyKpi.achievementRate'),
          ]}
          months={data.months}
          totalLabel={totalLabel}
          renderMonthCells={(m) => [
            // 当月は按分後目標の右に満額 capacity を括弧で併記（WIP 注記と同じ小サイズ・muted 色）
            <>
              {formatNum(m.ptTarget, 1)}
              {m.isCurrentMonth && m.ptTargetFull !== null && (
                <span className="ml-1 text-[10px] font-normal text-[var(--color-text-muted)]">
                  ({formatNum(m.ptTargetFull, 1)})
                </span>
              )}
            </>,
            <>
              {formatNum(m.ptActual, 1)}
              {m.isCurrentMonth && m.wipPt !== null && m.wipPt > 0 && (
                <span className="ml-1 text-[10px] font-normal text-[var(--color-text-muted)]">
                  ({t('members.quarterlyKpi.wipIncluded', { value: formatNum(m.wipPt, 1) })})
                </span>
              )}
            </>,
            <RateBadge rate={m.ptAchievementRate} inProgress={m.isCurrentMonth} />,
          ]}
          renderTotalCells={() => [
            formatNum(data.total.ptTarget, 1),
            formatNum(data.total.ptActual, 1),
            <RateBadge
              rate={data.total.ptAchievementRate}
              inProgress={data.includesCurrentMonth}
            />,
          ]}
        />
        <MetricTable
          sectionLabel={t('members.quarterlyKpi.combatPowerSection')}
          sectionLabelClass="text-[var(--color-qkpi-power)]"
          headerRowClass={POWER_HEADER_ROW_CLASS}
          columns={[
            t('members.quarterlyKpi.businessDays'),
            t('members.quarterlyKpi.expectedPower'),
            t('members.quarterlyKpi.actualPower'),
            t('members.quarterlyKpi.powerDiff'),
          ]}
          months={data.months}
          totalLabel={totalLabel}
          renderMonthCells={(m) => {
            // 当月は完了pt 戦闘力の右に「処理中pt 込み戦闘力」を括弧で併記（戦闘力サマリーと同形式）し、
            // 差分も処理中pt 込みの実績戦闘力を base に計算する
            const wipPower = actualPowerWithWip(m);
            return [
              formatPower(m.businessDays),
              formatPower(m.expectedPower),
              <>
                {formatPower(m.actualPower)}
                {wipPower !== null && (
                  <span className="ml-1 text-[10px] font-normal text-[var(--color-text-muted)]">
                    ({formatPower(wipPower)})
                  </span>
                )}
              </>,
              <DiffText diff={powerDiff(wipPower ?? m.actualPower, m.expectedPower)} />,
            ];
          }}
          renderTotalCells={() => [
            // Q合計の戦闘力は API 側で「Q合計 pt ÷ Q合計 稼働日数」として算出済み
            // （左の開発pt テーブルの Q合計 目標pt / 実績pt がそのまま分子。
            //   実績pt は処理中pt 込みのため、当月行のような括弧併記はしない）
            formatPower(data.total.businessDays),
            formatPower(data.total.expectedPower),
            formatPower(data.total.actualPower),
            <DiffText diff={powerDiff(data.total.actualPower, data.total.expectedPower)} />,
          ]}
        />
      </div>
      <div className="mt-2 space-y-0.5">
        {data.asOfDate !== null && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('members.quarterlyKpi.asOfNote', { date: data.asOfDate })}
          </p>
        )}
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('members.quarterlyKpi.powerAvgNote')}
        </p>
      </div>
    </div>
  );
}

/**
 * 月間pt カード（選択中の月の実績pt をピクセルフォントで大きく表示）
 * ヘッダーの月プルダウンで過去月へ遡れ、数字ボタンのクリックで当該月の pt 明細モーダルを開く。
 * hover で背景と数字の色を反転（実績集計エリアカードと同じく、内部コントロールは反転後も読める）
 * @graph-connects none
 */
function CurrentMonthPtCard({
  month,
  monthOptions,
  selectedMonthValue,
  onSelectMonth,
  onOpenDetail,
}: {
  /** 表示中の月データ（未取得時は null） */
  month: MemberQuarterlyKpiMonth | null;
  /** 選択可能な月（選択Qの非未来月）。空なら月プルダウンを出さない */
  monthOptions: { value: string; label: string }[];
  /** 選択中の月初日 (YYYY-MM-01)。未確定は null */
  selectedMonthValue: string | null;
  /** 月プルダウンの選択ハンドラ */
  onSelectMonth: (value: string | null) => void;
  /** 明細モーダルを開くハンドラ */
  onOpenDetail: () => void;
}) {
  const { t } = useTranslation();
  // 当月は「当月pt」、過去月は「月間pt」。選択中の月自体は月プルダウンが示す
  const isCurrent = month === null || month.isCurrentMonth;
  // カード大数の実績pt。過去月は完了分のみ（処理中pt は現在スナップショットのため当月のみ意味を持つ）。
  // 明細モーダルが過去月は完了のみを「（完了）」注記付きで合計表示するのに口径を合わせる。
  // ptActual = round1(完了 + 処理中)、wipPt = round1(処理中) とどちらも小数1位丸め済みのため差＝完了pt。
  const displayPt =
    month === null || month.ptActual === null
      ? null
      : isCurrent
        ? month.ptActual
        : month.ptActual - (month.wipPt ?? 0);
  return (
    <Card className="current-month-pt-card animate-content-reveal flex flex-col md:w-1/4 md:min-w-[180px]">
      <CardHeader
        title={
          <span data-card-text>
            {t(isCurrent ? 'members.quarterlyKpi.currentMonthPt' : 'members.quarterlyKpi.monthPt')}
          </span>
        }
        action={
          monthOptions.length > 0 ? (
            <div className="current-month-pt-actions w-[104px]">
              <Picker
                options={monthOptions}
                value={selectedMonthValue}
                onChange={onSelectMonth}
                aria-label={t('members.quarterlyKpi.monthSelect')}
              />
            </div>
          ) : undefined
        }
      />
      <CardContent className="flex flex-1 p-0">
        <button
          type="button"
          data-testid="current-month-pt-card"
          aria-label={t('members.quarterlyKpi.ptDetail.openLabel')}
          onClick={onOpenDetail}
          className="pt-open-button flex flex-1 flex-col items-center justify-center gap-3 py-6"
        >
          <div
            className="pt-number text-center leading-none"
            style={{ fontFamily: "'Press Start 2P', monospace" }}
            data-testid="current-month-pt"
          >
            <span className="text-4xl xl:text-5xl text-[var(--color-accent-primary)]">
              {formatNum(displayPt, 1)}
            </span>
            <span data-card-text className="ml-2 text-sm text-[var(--color-text-secondary)]">
              pt
            </span>
          </div>
          <p data-card-text className="text-[11px] text-[var(--color-text-muted)]">
            {t(
              isCurrent
                ? 'members.quarterlyKpi.currentMonthPtNote'
                : 'members.quarterlyKpi.monthPtNote',
            )}
          </p>
        </button>
      </CardContent>
    </Card>
  );
}

/** @graph-connects useMemberQuarterlyKpi [calls] メンバー四半期KPIフック */
export function MemberQuarterlyKpiTable({ email }: MemberQuarterlyKpiTableProps) {
  const { t } = useTranslation();
  // undefined = 当四半期（API のデフォルト解決に任せる）。右の四半期テーブル用
  const [selectedQuarter, setSelectedQuarter] = useState<string | undefined>(undefined);
  // 月間pt カードで選択中の月初日 (YYYY-MM-01)。null = 既定（当月）。四半期テーブルとは独立
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { data, isLoading, error } = useMemberQuarterlyKpi(email, selectedQuarter);

  // 月間pt カードは四半期テーブルのプルダウンから独立させ、availableQuarters を全過去月へ展開して
  // 単一の月プルダウンで遡れるようにする（当四半期の初月でも過去 Q の月が候補に出る）。
  // 当四半期を基準クエリ（selectedQuarter=undefined と dedupe）として availableQuarters と当月を得る
  const { data: baselineData } = useMemberQuarterlyKpi(email, undefined);
  const availableQuarters = baselineData?.availableQuarters ?? [];
  const currentMonthData = baselineData?.months.find((m) => m.isCurrentMonth) ?? null;
  const currentYm = currentMonthData ? currentMonthData.month.slice(0, 7) : jstCurrentYm();

  // availableQuarters を「当月以前の月初日（降順）」へ展開し、月プルダウンの候補にする
  const monthValues = pastMonthOptionsOf(availableQuarters, currentYm);
  const monthOptions = monthValues.map((v) => ({ value: v, label: monthLabel(v) }));
  // 選択中の月（既定は当月）。当月は 'YYYY-MM' 一致で判定（データ未取得でもタイトルを正しく出す）
  const effectiveMonth = selectedMonth ?? currentMonthData?.month ?? monthValues[0] ?? null;
  const isCurrentMonthSelected =
    effectiveMonth !== null && effectiveMonth.slice(0, 7) === currentYm;

  // 選択月が属する Q を遅延フェッチ（当四半期は undefined でデフォルト解決 = 基準クエリと dedupe）
  const ptQuarterParam =
    effectiveMonth !== null
      ? (quarterParamOfMonth(effectiveMonth, currentYm, availableQuarters) ?? undefined)
      : undefined;
  const { data: ptQuarterData } = useMemberQuarterlyKpi(email, ptQuarterParam);
  const selectedMonthData =
    effectiveMonth !== null
      ? (ptQuarterData?.months.find((m) => m.month === effectiveMonth) ?? null)
      : null;

  const quarterOptions = (data?.availableQuarters ?? []).map((q) => ({ value: q, label: q }));

  const hasAnyData =
    data !== undefined &&
    data.months.some(
      (m) =>
        m.ptTargetFull !== null ||
        m.ptActual !== null ||
        m.expectedPower !== null ||
        m.actualPower !== null,
    );

  return (
    <div className="flex flex-col md:flex-row gap-6 items-stretch">
      <CurrentMonthPtCard
        month={selectedMonthData}
        monthOptions={monthOptions}
        selectedMonthValue={effectiveMonth}
        onSelectMonth={(value) => {
          if (value !== null) setSelectedMonth(value);
        }}
        onOpenDetail={() => {
          setDetailOpen(true);
        }}
      />
      {effectiveMonth !== null && (
        <CurrentMonthPtDetailModal
          email={email}
          month={effectiveMonth}
          isCurrentMonth={isCurrentMonthSelected}
          open={detailOpen}
          onClose={() => {
            setDetailOpen(false);
          }}
        />
      )}
      <Card className="animate-content-reveal md:flex-1 min-w-0">
        <CardHeader
          title={t('members.quarterlyKpi.title')}
          action={
            quarterOptions.length > 0 ? (
              <div className="w-[150px]">
                <Picker
                  options={quarterOptions}
                  value={data?.quarter ?? null}
                  onChange={(value) => {
                    if (value !== null) setSelectedQuarter(value);
                  }}
                  aria-label={t('members.quarterlyKpi.quarterSelect')}
                />
              </div>
            ) : undefined
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="loading-spinner" />
            </div>
          ) : error ? (
            <ErrorState />
          ) : data !== undefined && hasAnyData ? (
            <QuarterlyKpiTableBody data={data} />
          ) : (
            <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
              {t('members.quarterlyKpi.noData')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
